/**
 * **A diverging comparison bar, on one symmetric scale** — "how far did this row move between A and
 * B", drawn left for less and right for more from a shared zero.
 *
 * Promoted out of `pages/client-detail/model/twoDates` on 27 September 2026, on its second
 * consumer: the plan overview's „Porównaj tygodnie" table draws the same bars as the Pomiary tab and
 * the Forma dock. The rules moved unchanged.
 *
 * ── The bars of one table share ONE scale, and that is the whole point of them ───────────────
 * Each bar is the change as a percent of the A value, so 8 cm at the waist and 1 cm at the biceps
 * are comparable. The scale is the table's largest change rounded up to an even percent, with a
 * floor — a client whose largest change is 0,3% would otherwise get a full-width bar for nothing —
 * and a bar is capped at `reach` of its track on each side so its own label has room beside it.
 */

/** The scale never drops below this, so a row that barely moved gets no full-width bar. */
const MIN_SCALE_PERCENT = 4;
/** A bar reaches at most this share of the track on each side, leaving room for its own label. */
const MAX_BAR_PERCENT = 40;

export interface DivergingBar {
  /** How far the bar reaches, as a percentage of the track's own width. Zero draws no bar. */
  readonly barPercent: number;
  readonly barSide: 'less' | 'more' | null;
}

/**
 * The full half of the track, in percent: the largest change in the table rounded up to an even
 * number, never under `MIN_SCALE_PERCENT`. A row with no percent (no pair, or an A of zero) does not
 * widen it.
 */
export function symmetricScalePercent(percents: readonly (number | null)[]): number {
  const largest = percents.reduce<number>(
    (widest, percent) => Math.max(widest, Math.abs(percent ?? 0)),
    MIN_SCALE_PERCENT,
  );
  return Math.ceil(largest / 2) * 2;
}

/**
 * How far one comparison bar reaches, as a share of its track, on a scale where `scalePercent` is a
 * full half. A zero change and a missing pair draw no bar at all — an invisible mark on a track is
 * indistinguishable from a row that could not be compared.
 */
export function divergingBar(
  percent: number | null,
  delta: number | null,
  scalePercent: number,
  reach: number = MAX_BAR_PERCENT,
): DivergingBar {
  if (percent === null || delta === null || delta === 0) return { barPercent: 0, barSide: null };
  return {
    barPercent: Math.min((Math.abs(percent) / scalePercent) * reach, reach),
    barSide: percent < 0 ? 'less' : 'more',
  };
}
